import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { SalesforceSyncRunDoc } from '@/lib/types';

interface RunRow extends SalesforceSyncRunDoc {
  id: string;
}

function fmtDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return rs ? `${m}m ${rs}s` : `${m}m`;
}

function fmtRelative(date: Date): string {
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function StatCell({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: 'rose' | 'emerald' | 'amber';
}) {
  const accentCls =
    accent === 'rose'
      ? 'text-rose-700'
      : accent === 'emerald'
        ? 'text-emerald-700'
        : accent === 'amber'
          ? 'text-amber-700'
          : 'text-slate-900';
  return (
    <div className="min-w-[88px]">
      <div className="label">{label}</div>
      <div className={`mt-0.5 text-lg font-semibold tabular-nums ${accentCls}`}>
        {value}
      </div>
    </div>
  );
}

export function SyncAnalyticsPanel() {
  const [last, setLast] = useState<SalesforceSyncRunDoc | null>(null);
  const [lastLoaded, setLastLoaded] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [showFailedIds, setShowFailedIds] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'meta', 'salesforceLastSync'),
      (snap) => {
        setLast(
          snap.exists() ? ((snap.data() as DocumentData) as SalesforceSyncRunDoc) : null,
        );
        setLastLoaded(true);
      },
      (err) => {
        setLastError(err.message);
        setLastLoaded(true);
      },
    );
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, 'meta/salesforceSyncRuns/runs'),
      orderBy('startedAt', 'desc'),
      limit(30),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRuns(
          snap.docs.map(
            (d) =>
              ({ id: d.id, ...(d.data() as DocumentData) }) as RunRow,
          ),
        );
      },
      (err) => setRunsError(err.message),
    );
    return unsub;
  }, []);

  const startedAt = last?.startedAt?.toDate();
  const finishedAt = last?.finishedAt?.toDate();
  const durationMs =
    startedAt && finishedAt ? finishedAt.getTime() - startedAt.getTime() : null;

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              Last Salesforce/Gong sync
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {finishedAt
                ? `Finished ${fmtRelative(finishedAt)} · ${finishedAt.toLocaleString()}`
                : lastLoaded
                  ? 'No sync recorded yet.'
                  : 'Loading…'}
            </p>
          </div>
          {durationMs != null && (
            <div className="text-right">
              <div className="label">Duration</div>
              <div className="text-sm font-medium tabular-nums text-slate-700">
                {fmtDuration(durationMs)}
              </div>
            </div>
          )}
        </div>

        {lastError && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
            {lastError}
          </div>
        )}

        {last && (
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
            <StatCell label="Cohort" value={last.cohortSize} />
            <StatCell label="Opps" value={last.opportunityCount} />
            <StatCell label="Scanned" value={last.callsScanned} />
            <StatCell label="Matched" value={last.callsMatched} />
            <StatCell
              label="Processed"
              value={last.callsProcessed}
              accent={last.callsProcessed > 0 ? 'emerald' : undefined}
            />
            <StatCell label="Skipped" value={last.callsSkipped} />
            <StatCell
              label="Failed"
              value={last.callsFailed ?? 0}
              accent={(last.callsFailed ?? 0) > 0 ? 'rose' : undefined}
            />
            {typeof last.emailsWritten === 'number' && (
              <StatCell
                label="SF emails"
                value={last.emailsWritten}
                accent={last.emailsWritten > 0 ? 'emerald' : undefined}
              />
            )}
          </div>
        )}

        {last?.failedCallIds && last.failedCallIds.length > 0 && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowFailedIds((v) => !v)}
              className="text-xs font-medium text-rose-700 hover:underline"
            >
              {showFailedIds ? 'Hide' : 'Show'} {last.failedCallIds.length} failed
              call id{last.failedCallIds.length === 1 ? '' : 's'}
            </button>
            {showFailedIds && (
              <ul className="mt-2 space-y-0.5 rounded-md bg-rose-50 p-3 font-mono text-xs text-rose-900">
                {last.failedCallIds.map((id) => (
                  <li key={id}>{id}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold">
          Recent runs ({runs.length})
        </div>
        {runsError && (
          <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-800">
            {runsError}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="whitespace-nowrap px-4 py-2 font-medium text-slate-600">
                  Started
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-right font-medium text-slate-600">
                  Duration
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-right font-medium text-slate-600">
                  Scanned
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-right font-medium text-slate-600">
                  Matched
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-right font-medium text-slate-600">
                  Processed
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-right font-medium text-slate-600">
                  Failed
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {runs.length === 0 && (
                <tr>
                  <td
                    className="px-4 py-6 text-center text-slate-500"
                    colSpan={6}
                  >
                    No history yet. Stats appear after the next scheduled sync.
                  </td>
                </tr>
              )}
              {runs.map((r) => {
                const started = r.startedAt?.toDate();
                const finished = r.finishedAt?.toDate();
                const dur =
                  started && finished
                    ? fmtDuration(finished.getTime() - started.getTime())
                    : '—';
                const failed = r.callsFailed ?? 0;
                return (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap px-4 py-2 text-slate-700">
                      {started ? started.toLocaleString() : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-slate-600">
                      {dur}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-slate-600">
                      {r.callsScanned}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-slate-600">
                      {r.callsMatched}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-slate-700">
                      {r.callsProcessed}
                    </td>
                    <td
                      className={`whitespace-nowrap px-4 py-2 text-right tabular-nums ${
                        failed > 0 ? 'font-semibold text-rose-700' : 'text-slate-500'
                      }`}
                    >
                      {failed}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
